import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useAuth } from "contexts/AuthContext";
import { fetchReportData, downloadReportToc } from "redux/actions/report";
import { RootState } from "redux/reducers";
import { isSubscribed } from "utils/subscriptions.utils";

import ReportHeader from "./ChildComponents/Header/ReportHeader";
import ReportMain from "./ChildComponents/Content/ReportMain";
import ReportSidebar from "./ChildComponents/Sidebar/ReportSidebar";
import ReportMenu from "./ChildComponents/Menu/ReportMenu";
import ReportFooter from "./ChildComponents/ReportFooter";
import Spinner from "../../Common/misc/Spinner";

import Header from "components/Header/Header";
import Footer from "components/Footer/Footer";

const Report: React.FC = () => {
  const dispatch = useDispatch();
  const { id = "" } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const { subscriptions, report } = useSelector((state: RootState) => state);

  const [loading, setLoading] = useState<boolean>(true);
  const [toggleLeftSide, setToggleLeftSide] = useState<boolean>(true);
  const [toggleRightSide, setToggleRightSide] = useState<boolean>(true);

  const subscribed = useMemo(
    () => isSubscribed(subscriptions, ~~id, currentUser, "active"),
    [subscriptions, id, currentUser]
  );

  useEffect(() => {
    if (!subscribed) {
      dispatch(downloadReportToc(id, setLoading));
    }
  }, [subscribed, dispatch, id]);

  useEffect(() => {
    if (id !== report.reportMeta.report_id) {
      dispatch(fetchReportData(id, currentUser.usergroupData.id, setLoading));
    }
  }, [dispatch, id, report.reportMeta.report_id, currentUser.usergroupData.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [report.currentChapter]);

  useEffect(() => {
    if (window.innerWidth > 1000 && window.innerWidth <= 1500) {
      setToggleRightSide(false);
    } else if (window.innerWidth <= 1000) {
      setToggleLeftSide(false);
      setToggleRightSide(false);
    }
  }, []);

  // Going from one report to another (e.g. from the search dropdown) keeps this page,
  // so until the new report loads, the store still holds the old one, whose chapters
  // don't match the link. Unsubscribed reports wait for their own details the same way.
  const otherReportShown = subscribed
    ? String(report.reportMeta.report_id) !== id
    : String(report.unsubscribed.content?.report_id) !== id;

  return loading || otherReportShown ? (
    <>
      <Header />
      <Spinner />
      <Footer />
    </>
  ) : (
    <>
      <Header />
      <Container>
        <ReportHeader
          subscribed={subscribed}
          toggleLeftSide={toggleLeftSide}
          setToggleLeftSide={setToggleLeftSide}
          toggleRightSide={toggleRightSide}
          setToggleRightSide={setToggleRightSide}
        />
        <InnerContainer id={"InnerContainer"}>
          <ReportMenu
            subscribed={subscribed}
            toggleLeftSide={toggleLeftSide}
            setToggleLeftSide={setToggleLeftSide}
          />
          <ReportMain
            subscribed={subscribed}
            usergroup={currentUser.usergroupData}
          />
          <ReportSidebar
            reportId={+id}
            toggleRightSide={toggleRightSide}
            setToggleRightSide={setToggleRightSide}
          />
        </InnerContainer>
        <ReportFooter
          scrollPositions={report.search.scrollPositions}
          searching={!!report.search.query}
        />
      </Container>
      <Footer />
    </>
  );
};

const Container = styled.div`
  background-color: ${(props) => props.theme.colors.lighterGrey};
  color: #000;
  /* min-height: 100vh; */

  /* 'auto' causes some performance issues when tables are being styled (GLUR22) */
  /* Set to 'hidden' for now and will change to 'auto' once tables are styled */
  overflow-y: hidden;
  
  width: 100%;
`;

const InnerContainer = styled.div`
  display: grid;
  grid-column-gap: 1rem;
  grid-template-columns: max-content auto max-content;
`;

export default Report;
